import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { tx } from '../i18n';

type Estado = 'datos' | 'conectando' | 'entrando' | 'abierta' | 'cerrada';

/**
 * La consola del propio appliance, por SSH, desde el panel (docs/centro-protocolo.md §2.5).
 * Viaja por el canal que el appliance abrió hacia afuera: no hace falta VPN ni puerto
 * abierto en la red del cliente. Como `soporte` entra con la llave del Centro, sin clave; a
 * los appliances de antes, con usuario y clave de su sshd (se usan y no se guardan).
 */
export function ConsolaSsh({ applianceId, nombre, conTitulo = true }: { applianceId: string; nombre: string; conTitulo?: boolean }) {
  const t = tx.consola;
  const [usuario, setUsuario] = useState('soporte');
  const [clave, setClave] = useState('');
  const [estado, setEstado] = useState<Estado>('datos');
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  // el error se lee en el cierre (que llega después): una ref, no el estado de ese momento
  const huboError = useRef(false);
  const pantalla = useRef<HTMLDivElement>(null);
  const ws = useRef<WebSocket | null>(null);
  const term = useRef<Terminal | null>(null);

  // al irse de la página no queda una sesión abierta en el appliance
  useEffect(() => () => ws.current?.close(), []);

  const entrar = (e: FormEvent) => {
    e.preventDefault();
    if (!usuario.trim()) return;
    setError('');
    setAviso('');
    huboError.current = false;
    setEstado('conectando');
    const x = new Terminal({
      cursorBlink: true,
      fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
      fontSize: 13.5,
      theme: { background: '#02060b', foreground: '#d8e7ef', cursor: '#3cc4bf', selectionBackground: 'rgba(60,196,191,0.3)' },
    });
    const ajuste = new FitAddon();
    x.loadAddon(ajuste);
    term.current = x;
    // se monta después de pintar el contenedor de la terminal
    requestAnimationFrame(() => {
      if (!pantalla.current) return;
      x.open(pantalla.current);
      try {
        ajuste.fit();
      } catch {
        // sin tamaño todavía (una pestaña oculta): queda el de omisión
      }
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const s = new WebSocket(`${proto}//${window.location.host}/api/consola?appliance=${encodeURIComponent(applianceId)}`);
      ws.current = s;
      // sin clave, el Centro entra con su llave (la que el appliance acepta para `soporte`)
      s.onopen = () => s.send(JSON.stringify({ tipo: 'abrir', usuario: usuario.trim(), clave: clave || undefined, columnas: x.cols, filas: x.rows }));
      s.onmessage = (ev) => {
        const m = JSON.parse(String(ev.data)) as { tipo: string; que?: Estado; d?: string; mensaje?: string };
        if (m.tipo === 'estado' && m.que) {
          setEstado(m.que);
          if (m.que === 'abierta') {
            setClave('');
            x.focus();
          }
        } else if (m.tipo === 'datos' && m.d) x.write(m.d);
        else if (m.tipo === 'error') {
          huboError.current = true;
          setError(m.mensaje ?? t.fallo);
        }
      };
      s.onclose = () => {
        // salió bien (exit, logout): la terminal no sirve más y se va; con un error se queda
        // a la vista, para poder leer qué pasó
        if (!huboError.current) {
          x.dispose();
          term.current = null;
          ws.current = null;
          setAviso(t.sesionCerrada);
          setEstado('datos');
          return;
        }
        setEstado((e) => (e === 'datos' ? e : 'cerrada'));
      };
      x.onData((d) => s.readyState === WebSocket.OPEN && s.send(JSON.stringify({ tipo: 'datos', d })));
      x.onResize(({ cols, rows }) => s.readyState === WebSocket.OPEN && s.send(JSON.stringify({ tipo: 'tamano', columnas: cols, filas: rows })));
      const obs = new ResizeObserver(() => {
        try {
          ajuste.fit();
        } catch {
          // la terminal ya no está
        }
      });
      obs.observe(pantalla.current);
      s.addEventListener('close', () => obs.disconnect());
    });
  };

  const otraVez = () => {
    const s = ws.current;
    ws.current = null;
    if (s) s.onclose = null; // cerrarla a mano no es "la sesión terminó"
    s?.close();
    term.current?.dispose();
    term.current = null;
    setError('');
    setEstado('datos');
  };

  if (estado === 'datos')
    return (
      <form className={`panel consola-datos${conTitulo ? '' : ' sin-titulo'}`} onSubmit={entrar}>
        {/* en su página la cabecera ya lo dice; en la pestaña del appliance, no */}
        {conTitulo && (
          <div className="consola-que">
            <h2 className="rotulo">{t.titulo(nombre)}</h2>
            <p className="d">{t.que}</p>
          </div>
        )}
        <label className="campo" htmlFor="consola-usuario">
          {t.usuario}
          <input
            id="consola-usuario"
            className="entrada num"
            autoComplete="off"
            spellCheck={false}
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
          />
        </label>
        <label className="campo" htmlFor="consola-clave">
          {t.clave}
          <input
            id="consola-clave"
            className="entrada"
            type="password"
            autoComplete="off"
            value={clave}
            placeholder={t.conLlave}
            onChange={(e) => setClave(e.target.value)}
          />
        </label>
        <button className="btn prim" disabled={!usuario.trim()} autoFocus>
          {t.entrar}
        </button>
        {aviso && !error && (
          <p className="nota consola-aviso" role="status">
            {aviso}
          </p>
        )}
        {error && (
          <p className="error-form" role="alert">
            {error}
          </p>
        )}
      </form>
    );

  return (
    <section className="panel consola">
      <div className="consola-barra">
        <span className={`punto ${estado === 'abierta' ? 'ok' : estado === 'cerrada' ? 'mal' : 'espera'}`} aria-hidden />
        <b className="num">{`${usuario}@${nombre}`}</b>
        <span className="d">{t.estados[estado]}</span>
        <span className="espacio" />
        <button type="button" className="btn" onClick={otraVez}>
          {estado === 'cerrada' ? t.otraVez : t.cerrar}
        </button>
      </div>
      {error && (
        <p className="error-form" role="alert">
          {error}
        </p>
      )}
      <div className="consola-pantalla" ref={pantalla} />
    </section>
  );
}
